import { Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { UIRouter } from '@uirouter/core';
import { firstValueFrom } from 'rxjs';
import { IdentityService } from '../core/authentication';
import { LicenseType } from '../core/authentication/models/licenses';
import { AccountMode, LicenseService } from '../core/license.service';
import { SpacesService } from '../common/components/spaces';
import { getCurrentSpaceFromPath, getPathForSpace } from '../common/components/spaces/index';
import { SpacetimeCatalogService } from '../features/spacetime/services/spacetime-catalog.service';
import {
    canOpenInsight,
    insightForState,
    SPACETIME_INSIGHT_TABS,
    SpacetimeInsightTab,
} from '../features/spacetime/utils/spacetime-feature-access.util';

/**
 * WS5 #10 Stage B B6 (THE ATOMIC CUT): native replacement for the AngularJS transition
 * hooks that were registered via `APP_MODULE.run(...)` in angularjs.module.ts:186-191.
 *
 * Passed as the `config` fn of `UIRouterModule.forRoot({ states, deferIntercept, otherwise, config })`
 * — @uirouter/angular invokes it with the singleton UIRouter + the root Injector during router
 * bootstrap, i.e. SYNCHRONOUSLY before main.ts's deferred `setTimeout(startUIRouter)` runs
 * `urlService.listen()/sync()`. So all 9 `onBefore` guards are installed before the first
 * transition fires (the install-before-sync ordering the AngularJS version relied on, where the
 * hooks installed inside `upgrade.bootstrap(...)` before main.ts re-enabled URL interception).
 *
 * Faithful 1:1 port of the 6 hook classes
 * (account-v2/src/app/global/transition-hooks/*, deleted in B7):
 *  - REGISTRATION ORDER is behavior: authentication-check before force-password-change-check
 *    (both match `requiresAuth`); permissions(staff) before permissions(perms). Preserved here.
 *  - dead deps dropped (force-password's unused `$location`, space-type's unused `ngrxStore`).
 *  - `$location.path()` → `router.urlService.path()` (both return the path, dropping the query).
 *  - license-check keeps its distinct `transition.abort()` + `stateService.go('hub', params)`
 *    shape (NOT `.target()` like the others) and the INSTALL-TIME `isFliptoStaff` snapshot.
 */
export function configureRouterHooks(router: UIRouter, injector: Injector): void {
    const transitions = router.transitionService;
    const identity = injector.get(IdentityService);
    const spacesService = injector.get(SpacesService);
    const licenseService = injector.get(LicenseService);
    const catalogService = injector.get(SpacetimeCatalogService);
    const store = injector.get(Store);

    // 1. authentication-check: anonymous user → sign-in (preserving the originating path).
    transitions.onBefore({
        to: (state) => state.data && state.data.requiresAuth
    }, (transition) => {
        if (identity.isAnonymous()) {
            return transition.router.stateService.target('sign-in', { redirectTo: router.urlService.path() });
        }
    });

    // 2. force-password-change-check: authenticated user flagged for password change → recover-password.
    transitions.onBefore({
        to: (state) => state.data && state.data.requiresAuth
    }, (transition) => {
        if (!identity.isAnonymous() && identity.user.isForceChangePassword) {
            return transition.router.stateService.target('recover-password');
        }
    });

    // 3. permissions-check (staff): flip.to-staff-only state hit by a non-staff user → home.
    transitions.onBefore({
        to: (state) => state.data && state.data.isFliptoStaff
    }, (transition) => {
        if (!identity.isFliptoStaff()) return transition.router.stateService.target('home');
        return true;
    });

    // 4. permissions-check (permissions): missing required permission → home.
    transitions.onBefore({
        to: (state) => state.data && state.data.requiredPermissions
    }, (transition) => {
        const permissionsRequired = transition.to().data.requiredPermissions;
        return identity.hasPermission(permissionsRequired) ? true : transition.router.stateService.target('home');
    });

    // 5. space-type-check: state requiring a space type the current space doesn't match → home.
    transitions.onBefore({
        to: (state) => !!state.data && state.data.requiredSpaceType
    }, (transition) => {
        const spacesRequired = Array.isArray(transition.to().data.requiredSpaceType)
            ? transition.to().data.requiredSpaceType
            : [transition.to().data.requiredSpaceType];
        if (!spacesService.current || spacesRequired.indexOf(spacesService.current.type) === -1) {
            return transition.router.stateService.target('home');
        }
    });

    // 6. params-check: required param missing on the target → home.
    transitions.onBefore({
        to: (state) => state.data && state.data.requiresParams
    }, (transition) => {
        const params = transition.to().data.requiresParams;
        if (params.some((param) => !transition.params()[param])) {
            return transition.router.stateService.target('home');
        }
    });

    // 7. license-check: snapshot staff status ONCE at install time (parity with the ctor-time
    //    snapshot in license-check.ts:9), then abort + redirect to hub if the license is missing.
    const isFliptoStaff = identity.isFliptoStaff();
    transitions.onBefore({
        to: (state) => state.data && state.data.requiredLicense
    }, (transition) => {
        const licenseRequired = transition.to().data.requiredLicense;
        const forceForFlipto = transition.to().data.forceForFlipto;
        if (licenseService.checkLicense(licenseService.getCurrentSpaceLicenses(), licenseRequired) || (forceForFlipto && isFliptoStaff)) {
            return true;
        } else {
            transition.abort();
            transition.router.stateService.go('hub', transition.params());
        }
    });

    // 8. account-mode check (1.73.x, ported from master's license-check.ts second hook): SpacetimeOnly
    //    accounts are confined to Spacetime. Staff and anonymous users exempt (live checks, NOT the
    //    install-time snapshot hook 7 uses — parity with master).
    const isSpaceScoped = (transition): boolean => {
        const params = transition.params();
        return !!(params && params.type && params.slug);
    };
    transitions.onBefore({}, (transition) => {
        if (identity.isAnonymous()) return;
        if (identity.isFliptoStaff()) return;
        if (licenseService.getAccountMode() === AccountMode.Full) return;
        if (!isSpaceScoped(transition)) return;

        const targetName: string = transition.to().name;
        if (targetName === 'spacetime' || targetName.startsWith('spacetime.')) return;

        transition.abort();
        if (!licenseService.checkCurrentSpaceLicense(LicenseType.Spacetime)) {
            transition.router.stateService.go('home', transition.params());
        } else {
            // The spacetime state lands on synopsis; hook 9 moves a locked one along.
            transition.router.stateService.go('spacetime', transition.params());
        }
    });

    // 9. insight-status check: a hotelier opens only an insight the space's catalog unlocks, else the first tab the nav
    //    shows (pin order, then catalog order; never Portfolio, whose own redirectTo could loop) or hub. Staff are exempt:
    //    the explainer opens a Locked one.
    transitions.onBefore({
        to: (state) => insightForState(state.name) !== null
    }, (transition) => {
        if (identity.isAnonymous() || identity.isFliptoStaff()) return;

        const insight = insightForState(transition.to().name);
        if (!insight) return;
        const toState = (name: string) => transition.router.stateService.target(name, transition.params());
        // A SpacetimeOnly account has nowhere outside Spacetime (hook 8 sends hub straight back here), so it
        // gets the spaces selector, as home shows it, rather than an empty shell on a cold load.
        const leave = () => {
            if (licenseService.getAccountMode() === AccountMode.Full) return toState('hub');
            store.dispatch({ type: '[Spaces] Set isCancelVisible', payload: false } as any);
            store.dispatch({ type: '[Spaces] Set isVisible', payload: true } as any);
            return false;
        };
        return firstValueFrom(catalogService.getCatalog$()).then(
            (catalog) => {
                const isOpen = (tab: SpacetimeInsightTab) => canOpenInsight(catalog, tab, false);
                if (isOpen(insight)) return true;
                const fallback = [...catalog.Pins, ...SPACETIME_INSIGHT_TABS]
                    .filter((tab): tab is SpacetimeInsightTab => tab !== 'portfolio' && (SPACETIME_INSIGHT_TABS as readonly string[]).includes(tab))
                    .find(isOpen);
                return fallback ? toState(`spacetime.${fallback}`) : leave();
            },
            leave,
        );
    });

    // WS5 #10 Stage B B6: recover from a dead-end lazy future-state placeholder. A space-mismatched
    // deep link (e.g. /p/{property}/account-settings — a Company/Mixed-only state NOT registered for a
    // Property space) is caught by the greedy `hub.**` catch-all future state. Under the former hybrid,
    // AngularJS' $location serialized the transitions and hub.** always upgraded to the clean `hub`. Under
    // pure @uirouter/angular the post-lazy-load upgrade races the not-yet-committed placeholder transition,
    // so the transition can settle on the component-less `*.**` placeholder (blank). Valid routes always
    // upgrade away from `*.**`, so a SUCCESS whose final state still ends in `.**` is exactly that dead end:
    // redirect to the clean space overview (URL-based, space-type-agnostic), reproducing the frozen hybrid's
    // redirect-to-overview for these (gate-documented VACUOUS) space-mismatched routes.
    transitions.onSuccess({}, (transition) => {
        const toName = transition.to().name;
        if (toName.endsWith('.**')) {
            // Force the clean parent state BY NAME ('hub.**' → 'hub'). A URL rewrite is a no-op
            // here — the unresolved remainder was already dropped, so the URL is already the space
            // root (/p/{slug}) yet still matched by the greedy `*.**` placeholder. Going by state
            // name bypasses URL matching and activates the real, component-bearing parent state,
            // reproducing the hybrid's hub.** → hub upgrade.
            transition.router.stateService.go(toName.slice(0, -3), transition.params());
        }
    });
}

/**
 * The `otherwise` rule — native port of angularjs.module.ts:139-148.
 * No matching route: send to `home` if there's no space in the path, else rewrite the URL to the
 * space's default path (which re-triggers routing into that space). Returning a TargetState/URL
 * string is the idiomatic @uirouter/angular equivalent of the v1 imperative `$state.go`/`$location.path`.
 */
export function routerOtherwise(matchValue: unknown, url: unknown, router: UIRouter) {
    const space = getCurrentSpaceFromPath();
    if (!space) {
        return router.stateService.target('home');
    }
    return getPathForSpace(space);
}
